import React from "react";
import { Check, PawPrint } from "lucide-react";
import SeoLandingLayout from "@/components/SeoLandingLayout";
import { LuxeOverline } from "@/components/LuxeOverline";
import { IMAGES } from "@/lib/site";

const FAQS = [
  {
    q: "Are dogs really welcome at Walnut Lakes?",
    a: "Yes — dogs are welcome across the whole park. They stay free on camping and touring pitches, and all three lakeside lodges are dog-friendly (£30 for one dog, £45 for two per stay).",
  },
  {
    q: "Is there somewhere safe to walk my dog?",
    a: "There's a half-mile fully enclosed dog walk right here on-site, plus fenland footpaths, riverside trails and quiet country lanes straight from the gate.",
  },
  {
    q: "How many dogs can I bring?",
    a: "Up to two well-behaved dogs per lodge or pitch. We ask that dogs stay on a lead around the park itself, and we can't accept dangerous breeds.",
  },
  {
    q: "Are there dog-friendly pubs nearby?",
    a: "Yes — Sutterton village is a 15-minute walk and has traditional pubs with dog-friendly gardens, and there are country kitchens and real-ale inns within a short drive.",
  },
  {
    q: "Is the park suitable for nervous dogs?",
    a: "Many owners tell us it's ideal — Walnut Lakes is adults-only with no children's play areas or big crowds, so the park stays calm and quiet throughout the season.",
  },
  {
    q: "Can dogs come fishing?",
    a: "Of course — your dog can join you on the bank while you fish either of our two private carp lakes. Just keep them on a lead near the water.",
  },
];

const PERKS = [
  "Dogs stay FREE on all camping and touring pitches",
  "All three lodges dog-friendly — £30 for 1 dog, £45 for 2 per stay",
  "Half-mile enclosed dog walk on-site",
  "Fenland footpaths and country lanes from the gate",
  "Adults-only park — calm and quiet for nervous dogs",
  "Dog-friendly pubs and farm shops within walking distance",
];

const DogFriendlyLincolnshire = () => (
  <SeoLandingLayout
    seo={{
      title: "Dog-Friendly Holidays in Lincolnshire · Dogs Stay Free on Pitches",
      description:
        "Dog-friendly holidays in Lincolnshire at Walnut Lakes — an adults-only park near Boston with a half-mile enclosed dog walk, dog-friendly lodges and pitches, and trails from the gate.",
      path: "/dog-friendly-holidays-lincolnshire",
    }}
    overline="Dog-friendly holidays"
    titlePre="Dog-friendly holidays in"
    titleEm="Lincolnshire."
    intro="Bring your best friend. Walnut Lakes is a quiet, adults-only holiday park in the Lincolnshire fens where dogs aren't just tolerated — they're expected. There's a half-mile enclosed dog walk on-site, footpaths straight from the gate, and dogs stay free on every camping and touring pitch."
    heroImage={IMAGES.dogWalk}
    heroAlt="Dog walking trail through the grounds at Walnut Lakes, a dog-friendly holiday park in Lincolnshire"
    faqs={FAQS}
  >
    {/* Perks */}
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <LuxeOverline>Four-legged guests</LuxeOverline>
        <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4">
          A holiday your dog will love as much as you
        </h2>
        <ul className="mt-8 space-y-3">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-3 text-sm text-[#D8CDBA]">
              <PawPrint size={15} className="text-[#E4B2A8] mt-0.5 flex-shrink-0" /> {p}
            </li>
          ))}
        </ul>
      </div>
      <img
        src={IMAGES.path}
        alt="Golden hour on the enclosed dog walk at Walnut Lakes, Lincolnshire"
        className="w-full h-[340px] object-cover rounded-2xl"
        loading="lazy"
      />
    </section>

    {/* Stay options */}
    <section className="bg-[#14100F] border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <LuxeOverline>Where you'll stay</LuxeOverline>
        <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4">
          Dog-friendly lodges and pitches
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="rounded-2xl bg-[#0A0A0A] border border-white/10 p-8">
            <h3 className="font-serif text-xl text-[#F5EFE6]">Lakeside lodges</h3>
            <p className="mt-3 text-sm leading-relaxed text-[#D8CDBA]">
              All three lodges — Heron, Kingfisher and Alpine — welcome up to two dogs.
              £30 for one dog or £45 for two per stay, with a private deck, fully-fitted
              kitchen and fishing on both lakes included. From £90 per night.
            </p>
          </div>
          <div className="rounded-2xl bg-[#0A0A0A] border border-white/10 p-8">
            <h3 className="font-serif text-xl text-[#F5EFE6]">Camping &amp; touring pitches</h3>
            <p className="mt-3 text-sm leading-relaxed text-[#D8CDBA]">
              Dogs stay completely free on every pitch — hardstanding with electric, grass
              with electric, or a simple tent pitch. From £24 per night with a one-night
              minimum stay.
            </p>
          </div>
        </div>
        <ul className="mt-8 space-y-3">
          {[
            "Dogs must be kept on a lead around the park",
            "Sorry — no dangerous breeds",
            "Please bring your dog's own bedding for the lodges",
          ].map((r) => (
            <li key={r} className="flex items-start gap-3 text-sm text-[#D8CDBA]">
              <Check size={16} className="text-[#E4B2A8] mt-0.5 flex-shrink-0" /> {r}
            </li>
          ))}
        </ul>
      </div>
    </section>

    {/* Location context */}
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <LuxeOverline>Walkies territory</LuxeOverline>
      <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4 max-w-2xl">
        Fenland rambles on the doorstep
      </h2>
      <p className="mt-6 max-w-2xl text-sm md:text-base leading-relaxed text-[#D8CDBA]">
        The park sits among quiet fenland lanes between Boston and Spalding — flat, open
        walking country with big skies. Riverside trails and nature reserves are minutes away
        by car, Sutterton village with its dog-friendly pubs is a 15-minute stroll, and the
        Lincolnshire coast's beaches make an easy day trip.
      </p>
    </section>
  </SeoLandingLayout>
);

export default DogFriendlyLincolnshire;
